import { sampleStateLabel } from './sampleModel.ts'
import { useState } from 'react'
import { useNavigate } from 'react-router'

import type { Sample } from '@ctcells/contract'
import { PATHS } from '@ctcells/core'
import { SAMPLES } from '@ctcells/fixtures'
import { maskPii, t } from '@ctcells/i18n'

import { Button } from '../../atoms/Button.tsx'
import { DataTable, type Column } from '../../atoms/DataTable.tsx'
import { SearchInput } from '../../atoms/SearchInput.tsx'
import { ShortId } from '../../atoms/ShortId.tsx'
import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import type { TableSort } from '../../atoms/tableSort.ts'
import { Toolbar } from '../../atoms/Toolbar.tsx'
import { ListPage } from '../../layout/ListPage.tsx'
import { useGroupTabLayout } from '../groupTabs.ts'
import { RESOURCE_TABS, type ResourceTabId } from '../resources/resourceTabs.ts'
import { Tabs, type TabItem } from '../../layout/Tabs.tsx'
import { canView, gate, type ScreenAccess } from '../access.ts'
import { fullDateTimeText } from '../format.ts'
import { OfflineNotice, RowLink } from '../resourceParts.tsx'
import { withPriority } from '../listColumnPriority.ts'
import { sampleStatusRank } from '../sortRanks.ts'
import { NaOr } from './SampleDetailScreen.tsx'

import '../screens.css'
import '../resource.css'

/**
 * 시료 목록 — 와이어프레임 `samples()` 2410행.
 *
 * 🔴 **환자 식별자·시료 ID 는 `view` 권한 없는 역할(`service`)에게 마스킹된다**(§5-5 · §19-5).
 *    fixture 는 원값을 그대로 담고, 가리는 일은 여기 표시 계층에서 `maskPii()` 가 한다.
 *
 * 🔴 목록은 **읽기 전용**이다. 값 변경은 상세에서만 한다(§14-4).
 *    첫 칸이 상세로 가는 **진짜 버튼**이다 — 와이어프레임의 `tr.rowlink` 는 키보드로 못 간다(§11-4-7).
 *    행 아무 칸이나 눌러도 열리지만(eric 2026-09-23) 그건 마우스·터치용 보태기다.
 */
export type SampleTab = 'all' | 'in_process' | 'completed' | 'discarded'

/**
 * 🔴 **정렬은 머리글이다**(eric 2026-09-30 「게시판 테이블의 컬럼 헤더 클릭하면.. 내림 차순 정렬.. 한번 더 클릭하면
 *    오름 차순」). 종전의 정렬 알약(최근 등록순 · 시료 번호 · 채혈일 · 처분)은 걷었다 — 두 정렬이 한 표에서 서로를
 *    덮는다. 채혈일은 열이 아니라 같이 빠졌다.
 * 처음 차례는 **등록 내림차순(최신 위)** 이다 — 알람 · 이력과 같다. 서버 목록도 등록 역순으로 온다.
 */
const SAMPLE_DEFAULT_SORT: TableSort = { key: 'createdAt', direction: 'desc' }

/**
 * 시료 상세로 가는 곳. 🔴 **첫 칸 링크와 행 클릭이 같은 함수를 쓴다** — 경로 문자열을 두 벌
 * 적으면 한쪽만 고쳐져 「ID 를 누르면 가는데 행을 누르면 딴 데로 간다」가 된다.
 */
const sampleDetailPath = (sample: Sample): string => `${PATHS.samples}/${sample.sample_id}`

export interface SamplesScreenProps {
  /**
   * 열린 「준비」 탭. 🔴 **라우트가 넘긴다** — 탭이 곧 주소다(`resources/resourceTabs.ts`).
   * **안 넘기면 무리 탭 줄을 안 그린다**(규칙은 `useGroupTabLayout` 주석) — 장비 관점 콘솔이
   * 그렇게 쓴다. 거기선 시료·레시피·랩웨어가 사이드바에 각자 서 있어서 탭 줄이 또 뜨면 안 된다.
   */
  resourceTab?: ResourceTabId | undefined
  samples?: readonly Sample[]
  /** 오른쪽 판에 열린 시료. 그 행이 «고른 행»이다(채움 + `aria-selected`) */
  selectedSampleId?: string | undefined
  /**
   * 목록을 **못 봤을 때의 사유**. 넘어오면 표의 빈 문구가 「시료 없음」 대신 이것이 된다.
   *
   * 🔴 **화면째 에러로 바꾸지 않는 이유는 «탭 줄»이다**(2026-09-21). `GET /samples` 는
   *    viewer 에게 403 인데(PII 접촉면) 「준비」 문이 이 화면으로 들어온다 —
   *    화면 전체를 사유 한 줄로 갈아 끼우면 **준비 탭 줄이 같이 사라져서 viewer 가
   *    레시피·랩웨어로 갈 길을 잃는다.** 서버는 그 둘에 200 을 준다.
   *    ⇒ 틀(탭 줄·제목)은 남기고 **표 안에만** 사유를 적는다.
   */
  notice?: string | undefined
  access?: ScreenAccess
  loading?: boolean
}

export function SamplesScreen({
  resourceTab,
  samples = SAMPLES,
  selectedSampleId,
  notice,
  access = {},
  loading = false,
}: SamplesScreenProps) {
  const navigate = useNavigate()
  const [tab, setTab] = useState<SampleTab>('all')
  // 🔴 「준비」 무리의 탭 줄(2026-09-18 eric 지시로 생겼다). 켜지면 위 줄은 준비 3탭, 아래 줄은
  //    이 화면 자체 탭이다 — 두 줄이 같은 굵기면 위계가 안 읽힌다. 꺼지면 자체 탭이 위로 올라온다
  const groupTabs = useGroupTabLayout(RESOURCE_TABS, resourceTab, 'samples', access)
  const [query, setQuery] = useState('')

  const visible = canView(access)

  const tabs: TabItem<SampleTab>[] = [
    { id: 'all', label: t('tab.samples.all'), count: samples.length },
    {
      id: 'in_process',
      label: t('tab.samples.in_process'),
      count: samples.filter((s) => s.status === 'in_process').length,
    },
    {
      id: 'completed',
      label: t('tab.samples.completed'),
      count: samples.filter((s) => s.status === 'completed').length,
    },
    {
      id: 'discarded',
      label: t('tab.samples.discarded'),
      count: samples.filter((s) => s.status === 'discarded').length,
    },
  ]

  /*
   * 머리줄 탭 한 줄에 **둘 중 하나**가 온다 — 무리 탭이 켜져 있으면 준비 3탭, 꺼져 있으면
   * 이 화면 자체 탭이다. 운영 3종(`BatchesScreen` 등)과 **같은 모양**으로 맞춰 둔다.
   */
  const pageTabs: TabItem<string>[] = groupTabs ? groupTabs.tabs : tabs
  const pageTabValue: string = groupTabs ? groupTabs.value : tab
  const onPageTabChange = (id: string): void => {
    if (groupTabs) groupTabs.onChange(id as ResourceTabId)
    else setTab(id as SampleTab)
  }

  const needle = query.trim().toLowerCase()
  const inTab = (sample: Sample): boolean => tab === 'all' || sample.status === tab
  const rows = samples.filter((sample) => {
    if (!inTab(sample)) return false
    if (needle === '') return true
    // 🔴 마스킹된 역할에게는 식별자 검색이 의미 없다 — 그래도 막지는 않는다(서버가 거른다)
    return [sample.sample_id, sample.patient_id, sample.batch_id]
      .filter((v): v is string => typeof v === 'string')
      .some((v) => v.toLowerCase().includes(needle))
  })

  /** 시료 등록은 `control` 권한이다. 제어락은 요구하지 않는다(계약 `POST /samples` 는 락 비대상) */
  const addGate = gate(access, { perm: 'control' })

  /*
   * 🔴 **열은 다섯이다 — 이름(시료 번호) · 상태 · 타입 · 작업 · 등록**(2026-10-29 eric 이 작업 열을 되살렸다 · 아래 주석).
   *    종전(넷)(eric 2026-09-30 「시료 번호, 종류, 처분, 등록 시각 이렇게
   *    하고.. 환자 번호랑.. 작업은... 상세 페이지에 넣어줘」 · 같은 날 「상태와 종류의 컬럼 순서 바꿔줘」 ·
   *    「종류 대신 타입으로」). 환자 번호 · 단계 · 현재 모듈은 목록에서 뺐고
   *    서랍 「시료」 카드가 적는다(`SampleDetailScreen` · `listRows`). 검색은 그대로 환자 번호 · 작업으로도 찾는다.
   * ⚠ 「현재 자리」 열은 여전히 없다(실측 0/349 · BR-31) — 값은 서랍의 「자리」 카드다.
   */
  const columns: Column<Sample>[] = [
    {
      key: 'sample_id',
      // 🔸 목록 첫 열 머리글은 「이름」(eric 2026-09-30 — 준비 세 목록 공통). 값 · 열 키는 그대로 시료 번호다
      header: t('column.name'),
      nowrap: true,
      /*
       * 🔴 **원값으로 센다.** 화면에 보이는 것은 역할에 따라 가려지지만(`maskPii`) 가린 글자로 정렬하면
       *    **가린 사람에게만 순서가 달라진다.**
       */
      sort: 'text',
      sortValue: (sample) => sample.sample_id,
      /*
       * 🔴 **미입력은 경고가 아니다**(2026-10-01 CTCells 회의 「시료번호만 필수 · 나머지는 참고용 선택 입력 · 빈칸은 NA ·
       *    검사 진행 가능」). 디자인팀 시안의 「▲ 미입력」 주의 배지와 행 왼쪽 주의 띠(`rowTone`)를 걷었다 — 되살리지 마라.
       *    빈 칸은 그 칸 자리에서 「NA」 로 적는다. 「미입력만 보기 N건」 알약도 걷었다(eric 2026-10-03 「버튼을 빼줘」) —
       *    되살리지 마라.
       */
      cell: (sample) => (
        <RowLink mono to={sampleDetailPath(sample)}>
          {maskPii(sample.sample_id, visible)}
        </RowLink>
      ),
    },
    /* 🔴 머리글은 「상태」다(eric 2026-09-30 「처분을 상태라는 텍스트로」) — 값은 종전 그대로 `Sample.status` */
    {
      key: 'status',
      header: t('column.status'),
      nowrap: true,
      // 진행 중 → 완료 → 폐기(탭 줄과 같은 차례)가 오름차순이다
      sort: (a, b) => sampleStatusRank(a.status) - sampleStatusRank(b.status),
      /* 🔴 상태 칸은 배지만이다(eric 2026-10-29) — 묶인 작업은 바로 뒤 「작업」 열이 적는다 */
      // 테 없는 알약(eric 2026-10-29 「시료쪽도.. 테두리 빼고」)
      cell: (sample) => (
        <StatusBadge flat label={sampleStateLabel(sample)} status={sample.status} />
      ),
    },
    /* 🔴 머리글은 「타입」이다(eric 2026-09-30 「시료에서.. 종류 대신 타입으로」) — 값은 `Sample.sample_type` */
    {
      key: 'type',
      header: t('column.sampleType'),
      nowrap: true,
      sort: 'text',
      sortValue: (sample) => sample.sample_type,
      /* 비었으면 「NA」(흐린 글자 · 회의 2026-10-01 「빈칸은 NA」) — 선택 입력 칸이라 「값 없음」이 정상이다 */
      cell: (sample) => <NaOr value={sample.sample_type} />,
    },
    /*
     * 🔴 「작업」 열 — 타입 다음(eric 2026-10-29 「작업 컬럼을 타입 다음으로 만들고.. 적용된 작업을 새로운 컬럼에」).
     *    짧은 표기(앞 8자 + 「…」) **글자만**이다 — 링크 · 단추를 두지 마라. 「어떤 라인을 눌러도 시료 상세 화면이
     *    나와야 돼」(같은 날) · 종전에는 상태 칸 안의 `RowLink` 가 작업 화면으로 갔다. 전체 id 는 `title` 툴팁.
     *    묶인 작업이 없으면 빈 칸이다(「—」 · 「NA」 가 아니다).
     */
    {
      key: 'batch',
      header: t('column.batch'),
      nowrap: true,
      sort: 'text',
      sortValue: (sample) => sample.batch_id,
      cell: (sample) =>
        sample.batch_id == null || sample.batch_id === '' ? null : (
          <ShortId value={sample.batch_id} />
        ),
    },
    {
      key: 'createdAt',
      /* 🔴 머리글은 「등록」이다(eric 2026-09-30 「등록 시각이라고 되어 있는 건.. 등록이라고만 해줘」) */
      header: t('column.registered'),
      nowrap: true,
      sort: 'time',
      sortValue: (sample) => sample.created_at,
      /*
       * 서버 시각(`created_at`) 그대로. 없으면 `—` — 지어내지 않는다.
       * 🔴 연-월-일 시:분:초 전부다(eric 2026-09-30 「등록 시각을.. 년월일 시분초로 해줘.. 알람 테이블처럼」).
       *    서랍 머리의 짧은 표기(`dateTimeText`)는 그대로다
       */
      cell: (sample) => <span className="muted">{fullDateTimeText(sample.created_at)}</span>,
    },
  ]

  return (
    <ListPage
      onTabChange={onPageTabChange}
      primaryAction={
        <Button
          size="pill"
          variant="primary"
          {...addGate}
          onClick={() => void navigate(PATHS.sampleNew)}
        >
          {t('action.addSample')}
        </Button>
      }
      tabValue={pageTabValue}
      tabs={pageTabs}
      tabsLabel={groupTabs ? t('screen.resourcesGroup.title') : t('screen.samples.title')}
      title={groupTabs ? t('screen.resourcesGroup.title') : t('screen.samples.title')}
      toolbar={
        <Toolbar label={t('screen.samples.title')}>
          {/*
           * 🔴 무리 탭이 켜져 있으면 화면 자체 탭(분류 칩)이 검색 왼쪽에 서고, 둘이 함께 준비 탭 줄 오른쪽에 선다 —
           *    같은 「작업 준비」의 레시피 · 랩웨어와 같은 자리다(eric 2026-10-06). 꺼져 있으면 자체 탭이 머리줄에 있다
           */}
          {groupTabs ? (
            <Tabs
              items={tabs}
              label={t('screen.samples.title')}
              onChange={setTab}
              value={tab}
              variant="sub"
            />
          ) : null}
          {/* 눈에 보이는 글자는 「검색」뿐(eric 2026-09-30 · 작업 · 이력과 같다). 시료 키는 접근성 이름으로 남는다 */}
          <SearchInput
            label={t('filter.search.samples')}
            onChange={setQuery}
            placeholder={t('filter.search.batchesPlaceholder')}
            value={query}
          />
        </Toolbar>
      }
      toolbarInTabs={groupTabs !== null}
    >
      <OfflineNotice shown={access.offline === true} />

      <DataTable
        caption={t('screen.samples.title')}
        /* 🔴 공간이 모자라면 덜 중요한 열부터 빠진다 — 차례는 `listColumnPriority.ts` 한 곳 */
        columns={withPriority('samples', columns)}
        /* 열이 다섯이라 남는 폭을 고르게 나눈다 — 등록 시각 뒤만 크게 비지 않게(작업 · 이력 목록과 같은 꼴) */
        columnWidths="even"
        defaultSort={SAMPLE_DEFAULT_SORT}
        /*
         * 🔴 못 봤으면 「시료 없음」이라고 단언하지 않는다 — 사유를 «다른 생김새»로 적는다.
         *    종전에는 사유를 `emptyLabel` 에 밀어 넣어서 viewer 의 「권한이 없습니다」가
         *    0건과 똑같은 회색 한 줄이었다(2026-09-22).
         */
        emptyLabel={t('state.empty.samples')}
        loading={loading}
        {...(notice === undefined ? {} : { notice })}
        /*
         * 🔴 **행 아무 칸이나 눌러도 열린다**(eric 2026-09-23). 첫 칸의 `RowLink` 는 그대로다 —
         *    키보드로 가는 길은 그 버튼뿐이다(`DataTable.onRowClick` 주석).
         */
        onRowClick={(sample) => void navigate(sampleDetailPath(sample))}
        rowKey={(sample) => sample.sample_id}
        rows={rows}
        selected={(sample) => sample.sample_id === selectedSampleId}
      />
    </ListPage>
  )
}
